import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Boxes, Plus, Trash2, UserMinus } from 'lucide-react';
import * as React from 'react';

import { UserSelect } from '~/components/admin-entity-select';
import { CreateGroupModal } from '~/components/create-group-modal';
import { Button } from '~/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '~/components/ui/card';
import { Input } from '~/components/ui/input';
import { Skeleton } from '~/components/ui/skeleton';
import { api, type UsersResponse } from '~/lib/api';
import { requireOwnerOrAdmin } from '~/lib/route-auth';

export const Route = createFileRoute('/settings/groups')({
  beforeLoad: async () => {
    await requireOwnerOrAdmin();
  },
  component: GroupsSettingsPage
});

interface GroupRow {
  id: string;
  roomId: string | null;
  name: string;
  createdAt: string;
  roomIds: string[];
}

interface GroupsResponse {
  groups: GroupRow[];
}

interface MembersResponse {
  members: Array<{ id: string; email: string; role: string }>;
}

function GroupsSettingsPage() {
  const queryClient = useQueryClient();
  const { data: roomsData } = useQuery({
    queryKey: ['rooms'],
    queryFn: () =>
      api.get<{ rooms: Array<{ id: string; name: string }> }>('/api/rooms')
  });
  const roomNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const room of roomsData?.rooms ?? []) {
      map.set(room.id, room.name);
    }
    return map;
  }, [roomsData?.rooms]);

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UsersResponse>('/api/users')
  });

  const users = (usersData?.users ?? []).filter((u) => u.deactivatedAt == null);

  const { data: groupsData, isLoading: groupsLoading } = useQuery({
    queryKey: ['groups'],
    queryFn: () => api.get<GroupsResponse>('/api/groups')
  });

  const [selectedGroupId, setSelectedGroupId] = React.useState<string | null>(
    null
  );
  React.useEffect(() => {
    const first = groupsData?.groups[0]?.id;
    if (first && !selectedGroupId) {
      setSelectedGroupId(first);
    }
    if (
      selectedGroupId &&
      !groupsData?.groups.some((g) => g.id === selectedGroupId)
    ) {
      setSelectedGroupId(first ?? null);
    }
  }, [groupsData?.groups, selectedGroupId]);

  const activeGroupId = selectedGroupId ?? groupsData?.groups[0]?.id ?? null;

  const { data: membersData, isLoading: membersLoading } = useQuery({
    queryKey: ['groups', activeGroupId, 'members'],
    queryFn: () =>
      api.get<MembersResponse>(`/api/groups/${activeGroupId}/members`),
    enabled: !!activeGroupId
  });

  const [createGroupOpen, setCreateGroupOpen] = React.useState(false);
  const [renameId, setRenameId] = React.useState<string | null>(null);
  const [renameValue, setRenameValue] = React.useState('');
  const [memberUserId, setMemberUserId] = React.useState('');

  const invalidateGroups = () => {
    void queryClient.invalidateQueries({ queryKey: ['groups'] });
  };

  const patchGroup = useMutation({
    mutationFn: (args: { id: string; name: string }) =>
      api.patch(`/api/groups/${args.id}`, { name: args.name }),
    onSuccess: () => {
      setRenameId(null);
      invalidateGroups();
    }
  });

  const deleteGroup = useMutation({
    mutationFn: (id: string) => api.delete(`/api/groups/${id}`),
    onSuccess: () => {
      setSelectedGroupId(null);
      invalidateGroups();
    }
  });

  const addMember = useMutation({
    mutationFn: (args: { groupId: string; userId: string }) =>
      api.post(`/api/groups/${args.groupId}/members`, { userId: args.userId }),
    onSuccess: (_, args) => {
      setMemberUserId('');
      void queryClient.invalidateQueries({
        queryKey: ['groups', args.groupId, 'members']
      });
    }
  });

  const removeMember = useMutation({
    mutationFn: (args: { groupId: string; userId: string }) =>
      api.delete(`/api/groups/${args.groupId}/members/${args.userId}`),
    onSuccess: (_, args) => {
      void queryClient.invalidateQueries({
        queryKey: ['groups', args.groupId, 'members']
      });
    }
  });

  const members = membersData?.members ?? [];
  const memberIds = new Set(members.map((m) => m.id));
  const addableUsers = users.filter((u) => !memberIds.has(u.id));

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Boxes className="h-5 w-5" />
        <h2 className="text-2xl font-bold">Groups</h2>
      </div>

      <p className="text-sm text-muted-foreground">
        Groups are organization-wide. Grant room access with Share on a room or
        folder, then add members here or invite them to a group from{' '}
        <Link
          to="/settings/users"
          className="font-medium text-primary underline-offset-4 hover:underline"
        >
          Users
        </Link>
        .
      </p>

      <Card>
        <CardHeader className="space-y-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 space-y-1.5">
              <CardTitle>All groups</CardTitle>
              <CardDescription>
                Rename, delete, or pick a group to manage members
              </CardDescription>
            </div>
            <Button
              type="button"
              size="sm"
              className="shrink-0 gap-1.5"
              onClick={() => setCreateGroupOpen(true)}
            >
              <Plus className="h-4 w-4" />
              New group
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {groupsLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : !groupsData?.groups.length ? (
            <p className="text-sm text-muted-foreground">No groups yet.</p>
          ) : (
            <ul className="space-y-2">
              {groupsData.groups.map((g) => (
                <li
                  key={g.id}
                  className={`flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between ${
                    activeGroupId === g.id ? 'border-primary bg-primary/5' : ''
                  }`}
                >
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left text-sm font-medium"
                    onClick={() => setSelectedGroupId(g.id)}
                  >
                    {renameId === g.id ? (
                      <span className="sr-only">Renaming</span>
                    ) : null}
                    {renameId === g.id ? (
                      <Input
                        className="mt-1 max-w-xs"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const name = renameValue.trim();
                            if (name) patchGroup.mutate({ id: g.id, name });
                          }
                          if (e.key === 'Escape') {
                            setRenameId(null);
                          }
                        }}
                        autoFocus
                      />
                    ) : (
                      <span className="block truncate">{g.name}</span>
                    )}
                    <span className="block text-xs font-normal text-muted-foreground">
                      {g.roomIds.length > 0 ? (
                        <>
                          Room access:{' '}
                          {g.roomIds
                            .map((id) => roomNameById.get(id) ?? 'Unknown room')
                            .join(', ')}
                        </>
                      ) : (
                        <>No room access yet — use Share on a room</>
                      )}
                      <span className="mt-0.5 block">
                        Created{' '}
                        {new Date(g.createdAt).toLocaleDateString(undefined, {
                          dateStyle: 'medium'
                        })}
                      </span>
                    </span>
                  </button>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    {renameId === g.id ? (
                      <>
                        <Button
                          type="button"
                          size="sm"
                          disabled={patchGroup.isPending || !renameValue.trim()}
                          onClick={() => {
                            const name = renameValue.trim();
                            if (name) patchGroup.mutate({ id: g.id, name });
                          }}
                        >
                          Save
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setRenameId(null)}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setRenameId(g.id);
                            setRenameValue(g.name);
                          }}
                        >
                          Rename
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          disabled={deleteGroup.isPending}
                          onClick={() => {
                            if (window.confirm(`Delete group “${g.name}”?`)) {
                              deleteGroup.mutate(g.id);
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {activeGroupId ? (
        <Card>
          <CardHeader>
            <CardTitle>Members</CardTitle>
            <CardDescription>Users in the selected group</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="min-w-0 flex-1 space-y-2">
                <label htmlFor="add-member" className="text-sm font-medium">
                  Add member
                </label>
                <UserSelect
                  id="add-member"
                  value={memberUserId}
                  onValueChange={setMemberUserId}
                  users={addableUsers}
                  placeholder={
                    addableUsers.length > 0
                      ? 'Choose user...'
                      : 'All users added'
                  }
                  ariaLabel="Add group member"
                  disabled={addMember.isPending || addableUsers.length === 0}
                />
              </div>
              <Button
                type="button"
                disabled={addMember.isPending || !memberUserId}
                onClick={() => {
                  if (!activeGroupId || !memberUserId) return;
                  addMember.mutate({
                    groupId: activeGroupId,
                    userId: memberUserId
                  });
                }}
              >
                Add
              </Button>
            </div>

            {membersLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : members.length === 0 ? (
              <p className="text-sm text-muted-foreground">No members yet.</p>
            ) : (
              <ul className="divide-y rounded-md border">
                {members.map((m) => (
                  <li
                    key={m.id}
                    className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
                  >
                    <span className="min-w-0 truncate">
                      {m.email}
                      <span className="ml-2 text-muted-foreground capitalize">
                        ({m.role})
                      </span>
                    </span>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="shrink-0"
                      disabled={removeMember.isPending}
                      aria-label={`Remove ${m.email}`}
                      onClick={() => {
                        if (!activeGroupId) return;
                        removeMember.mutate({
                          groupId: activeGroupId,
                          userId: m.id
                        });
                      }}
                    >
                      <UserMinus className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      ) : null}

      <CreateGroupModal
        open={createGroupOpen}
        onOpenChange={setCreateGroupOpen}
      />
    </div>
  );
}
